import React, { useState, useMemo } from "react";
import { printB2BDocument, printBonDeLivraison, printEtiquette } from "../../data/b2bDocuments";
import { deleteB2BSale, saveB2BSale, deductStockForFacture, cloneSaleForRepeat, generateLegalFactureNumber } from "../../data/b2bSales";

export default function B2BSalesList({ sales, setSales, clients, catalogProducts, onRequestPayment }) {
  const [expandedSaleId, setExpandedSaleId] = useState(null);
  const [editingSale, setEditingSale] = useState(null); // The copy of the sale we are editing inline
  const [searchQuery, setSearchQuery] = useState("");

  const handleExpand = (sale) => {
    if (expandedSaleId === sale.id) {
      setExpandedSaleId(null);
      setEditingSale(null);
    } else {
      setExpandedSaleId(sale.id);
      setEditingSale(JSON.parse(JSON.stringify(sale))); // Deep clone for editing
    }
    setSearchQuery("");
  };

  const handlePrintDocument = (sale, docType) => {
    const client = clients.find(c => c.id === sale.clientId);
    if (docType === "facture") printB2BDocument(sale, client);
    else if (docType === "bl") printBonDeLivraison(sale, client);
    else if (docType === "etiquette") printEtiquette(sale, client);
  };

  const handleDeliver = async (sale) => {
    if (!window.confirm("Confirmer la livraison ? Cela déduira le stock pour cette facture.")) return;
    try {
      const updated = await deductStockForFacture(sale);
      setSales(sales.map(s => s.id === updated.id ? updated : s));
    } catch (err) {
      alert("Erreur lors de la livraison : " + err.message);
    }
  };

  const handleRepeat = async (sale) => {
    const cloned = cloneSaleForRepeat(sale, catalogProducts);
    // Automatically open the new cloned sale in edit mode at the top of the list
    setSales([cloned, ...sales]);
    setExpandedSaleId(cloned.id);
    setEditingSale(cloned);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await deleteB2BSale(id);
    setSales(sales.filter(s => s.id !== id));
  };

  // --- Inline Editing Logic ---
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...editingSale.items];
    updatedItems[index][field] = parseFloat(value) || 0;
    
    // Auto-calculate line totals
    const qty = updatedItems[index].quantity || 1;
    const salePrice = updatedItems[index].salePrice || 0;
    const tva = updatedItems[index].tvaRate || 0.055;
    
    updatedItems[index].lineTotalHT = qty * salePrice;
    updatedItems[index].lineTotalTTC = qty * salePrice * (1 + tva);
    
    setEditingSale({ ...editingSale, items: updatedItems });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = editingSale.items.filter((_, i) => i !== index);
    setEditingSale({ ...editingSale, items: updatedItems });
  };

  const filteredProducts = useMemo(() => {
    if (!searchQuery) return [];
    return (catalogProducts || []).filter(p => 
      (p.name || "").toLowerCase().includes(searchQuery.toLowerCase()) || 
      (p.barcode || "").includes(searchQuery)
    ).slice(0, 10);
  }, [searchQuery, catalogProducts]);

  const handleAddProduct = (product) => {
    const updatedItems = [...editingSale.items];
    const existingIndex = updatedItems.findIndex(i => i.productId === product.id);
    
    if (existingIndex >= 0) {
      updatedItems[existingIndex].quantity += 1;
      const qty = updatedItems[existingIndex].quantity;
      const price = updatedItems[existingIndex].salePrice;
      const tva = updatedItems[existingIndex].tvaRate || 0.055;
      updatedItems[existingIndex].lineTotalHT = qty * price;
      updatedItems[existingIndex].lineTotalTTC = qty * price * (1 + tva);
    } else {
      updatedItems.push({
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitCost: product.purchasePrice || product.costPrice || 0,
        salePrice: product.price || 0,
        tvaRate: 0.055,
        lineTotalHT: product.price || 0,
        lineTotalTTC: (product.price || 0) * 1.055
      });
    }
    
    setEditingSale({ ...editingSale, items: updatedItems });
    setSearchQuery("");
  };

  const calculateMargin = (cost, sale) => {
    if (sale <= 0) return 0;
    return (((sale - cost) / sale) * 100).toFixed(1);
  };

  const persistSale = async (saleToSave) => {
    let totalHT = 0;
    let totalTVA = 0;
    saleToSave.items.forEach(item => {
      totalHT += item.lineTotalHT || 0;
      totalTVA += (item.lineTotalHT || 0) * (item.tvaRate || 0.055);
    });

    const finalSale = {
      ...saleToSave,
      totalHT,
      totalTVA,
      totalTTC: totalHT + totalTVA,
    };

    // Generate legal number if it does not have one
    if (!finalSale.documentNumber && finalSale.documentType === "facture") {
      finalSale.documentNumber = generateLegalFactureNumber(sales);
    }

    return await saveB2BSale(finalSale);
  };

  const handleSaveChanges = async () => {
    try {
      const saved = await persistSale(editingSale);
      setSales(sales.map(s => s.id === saved.id ? saved : s));
      setExpandedSaleId(null);
      setEditingSale(null);
    } catch (err) {
      alert("Erreur lors de l'enregistrement : " + err.message);
    }
  };

  const handleSaveAndPrint = async () => {
    if (!editingSale.clientId) {
      alert("Veuillez sélectionner un client avant d'imprimer.");
      return;
    }
    if (!editingSale.items || editingSale.items.length === 0) {
      alert("Veuillez ajouter au moins un produit avant d'imprimer.");
      return;
    }
    try {
      const saved = await persistSale(editingSale);
      setSales(sales.map(s => s.id === saved.id ? saved : s));
      setExpandedSaleId(null);
      setEditingSale(null);
      const client = clients.find(c => c.id === saved.clientId);
      printB2BDocument(saved, client);
    } catch (err) {
      alert("Erreur lors de l'enregistrement : " + err.message);
    }
  };

  const handleCreateNew = () => {
    const newSale = {
      id: "b2b-" + Date.now(),
      clientId: "",
      documentType: "facture",
      documentNumber: "",
      status: "pending",
      items: [],
      createdAt: new Date().toISOString()
    };
    setSales([newSale, ...sales]);
    setExpandedSaleId(newSale.id);
    setEditingSale(newSale);
  };

  const getClientName = (clientId) => {
    const c = clients.find(x => x.id === clientId);
    return c ? c.companyName || c.name : "Sélectionner un client...";
  };

  const totalSales = (sales || []).filter(s => s.documentType === "facture").reduce((acc, s) => acc + (s.totalTTC || 0), 0);

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Factures & Devis</h3>
        <button className="btn btn-primary" onClick={handleCreateNew}>
          <i className="fas fa-plus me-2"></i>Nouveau Document
        </button>
      </div>

      <div className="table-responsive bg-white rounded shadow-sm">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{width: "40px"}}></th>
              <th>Réf</th>
              <th>Date</th>
              <th>Type</th>
              <th>Client</th>
              <th>Total TTC</th>
              <th>Statut</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(sales || []).map(sale => {
              const isDevis = sale.documentType === "devis";
              const isDelivered = sale.status === "delivered" || sale.status === "paid";
              const isPaid = sale.status === "paid";
              const isExpanded = expandedSaleId === sale.id;

              return (
                <React.Fragment key={sale.id}>
                  {/* MAIN ROW */}
                  <tr className={isExpanded ? "table-active" : ""}>
                    <td className="text-center" style={{ cursor: "pointer" }} onClick={() => handleExpand(sale)}>
                      <i className={`fas fa-chevron-${isExpanded ? "down" : "right"} text-muted`}></i>
                    </td>
                    <td onClick={() => handleExpand(sale)} style={{ cursor: "pointer" }}>
                      <strong>{sale.documentNumber || "NOUVEAU"}</strong>
                    </td>
                    <td>{new Date(sale.createdAt || sale.date).toLocaleDateString("fr-FR")}</td>
                    <td>
                      <span className={`badge ${isDevis ? "bg-secondary" : "bg-primary"}`}>
                        {isDevis ? "DEVIS" : "FACTURE"}
                      </span>
                    </td>
                    <td>{getClientName(sale.clientId)}</td>
                    <td><strong>{(sale.totalTTC || 0).toFixed(2)} €</strong></td>
                    <td>
                      {isDevis ? (
                        <span className="badge bg-warning text-dark">{sale.status}</span>
                      ) : (
                        <>
                          {isDelivered ? <span className="badge bg-info me-1">Livré</span> : <span className="badge bg-warning text-dark me-1">Non livré</span>}
                          {isPaid ? <span className="badge bg-success">Payé</span> : <span className="badge bg-danger">Non payé</span>}
                        </>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="dropdown d-inline-block me-1">
                        <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                          <i className="fas fa-print"></i>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          <li><button className="dropdown-item" onClick={() => handlePrintDocument(sale, "facture")}><i className="fas fa-file-invoice me-2"></i>Imprimer Facture / Devis</button></li>
                          <li><button className="dropdown-item" onClick={() => handlePrintDocument(sale, "bl")}><i className="fas fa-truck me-2"></i>Imprimer Bon Livraison</button></li>
                          <li><button className="dropdown-item" onClick={() => handlePrintDocument(sale, "etiquette")}><i className="fas fa-tag me-2"></i>Imprimer Étiquette Colis</button></li>
                        </ul>
                      </div>
                      <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleRepeat(sale)} title="Répéter cette commande">
                        <i className="fas fa-redo"></i>
                      </button>
                      {!isDevis && !isDelivered && (
                        <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleDeliver(sale)} title="Marquer livré (Déduit le stock)">
                          <i className="fas fa-truck"></i>
                        </button>
                      )}
                      {!isDevis && !isPaid && (
                        <button className="btn btn-sm btn-outline-success me-1" onClick={() => onRequestPayment(sale)} title="Encaisser paiement">
                          <i className="fas fa-euro-sign"></i>
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(sale.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>

                  {/* EXPANDED INLINE EDITING VIEW */}
                  {isExpanded && editingSale && (
                    <tr>
                      <td colSpan="8" className="p-0 border-bottom-0">
                        <div className="bg-light p-4 border-start border-4 border-primary shadow-inner">
                          
                          {/* Header controls for new/edit */}
                          <div className="row mb-3">
                            <div className="col-md-4">
                              <label className="form-label text-muted small mb-1">Client</label>
                              <select 
                                className="form-select form-select-sm" 
                                value={editingSale.clientId} 
                                onChange={(e) => setEditingSale({...editingSale, clientId: e.target.value})}
                              >
                                <option value="">Sélectionner un client...</option>
                                {(clients || []).map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                              </select>
                            </div>
                            <div className="col-md-3">
                              <label className="form-label text-muted small mb-1">Type Document</label>
                              <select 
                                className="form-select form-select-sm" 
                                value={editingSale.documentType} 
                                onChange={(e) => setEditingSale({...editingSale, documentType: e.target.value})}
                              >
                                <option value="facture">Facture</option>
                                <option value="devis">Devis</option>
                              </select>
                            </div>
                          </div>

                          {/* Items Table */}
                          <table className="table table-sm table-bordered bg-white">
                            <thead className="table-secondary text-muted">
                              <tr>
                                <th>Produit</th>
                                <th style={{width:"10%"}}>Qté</th>
                                <th style={{width:"15%"}}>Dernier Coût (HT)</th>
                                <th style={{width:"15%"}}>Prix Vente (HT)</th>
                                <th style={{width:"10%"}}>Marge</th>
                                <th style={{width:"12%"}}>Total HT</th>
                                <th style={{width:"5%"}}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {editingSale.items.map((item, idx) => {
                                const margin = calculateMargin(item.unitCost, item.salePrice);
                                const isLowMargin = parseFloat(margin) < 15;
                                return (
                                  <tr key={idx} className={item.costChanged ? "table-warning" : ""}>
                                    <td className="align-middle">
                                      <strong>{item.productName}</strong>
                                      {item.costChanged && <span className="ms-2 badge bg-danger" title={`Ancien coût: ${item.oldUnitCost} €`}>Le coût d'achat a changé !</span>}
                                    </td>
                                    <td>
                                      <input type="number" className="form-control form-control-sm" value={item.quantity} onChange={(e) => handleItemChange(idx, "quantity", e.target.value)} min="1" />
                                    </td>
                                    <td className="align-middle">
                                      <input
                                        type="number"
                                        className="form-control form-control-sm text-muted"
                                        value={item.unitCost}
                                        onChange={(e) => handleItemChange(idx, "unitCost", e.target.value)}
                                        step="0.01"
                                        min="0"
                                        title="Dernier coût d'achat du catalogue. Modifiable pour tenir compte des fluctuations."
                                      />
                                    </td>
                                    <td>
                                      <input type="number" className="form-control form-control-sm fw-bold text-primary" value={item.salePrice} onChange={(e) => handleItemChange(idx, "salePrice", e.target.value)} step="0.01" />
                                    </td>
                                    <td className="align-middle">
                                      <span className={`badge ${isLowMargin ? "bg-warning text-dark" : "bg-success"}`}>{margin}%</span>
                                    </td>
                                    <td className="align-middle fw-bold">
                                      {(item.quantity * item.salePrice).toFixed(2)} €
                                    </td>
                                    <td className="text-center align-middle">
                                      <button type="button" className="btn btn-sm btn-outline-danger border-0" onClick={() => handleRemoveItem(idx)}><i className="fas fa-times"></i></button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>

                          {/* Quick Add Search */}
                          <div className="position-relative mb-4 w-50">
                            <input 
                              type="text" 
                              className="form-control form-control-sm bg-white" 
                              placeholder="+ Ajouter un produit (Recherche ou Scan...)" 
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {filteredProducts.length > 0 && (
                              <ul className="list-group position-absolute w-100 mt-1 shadow" style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}>
                                {filteredProducts.map(product => (
                                  <button 
                                    type="button" 
                                    key={product.id} 
                                    className="list-group-item list-group-item-action py-1 d-flex justify-content-between"
                                    onClick={() => handleAddProduct(product)}
                                  >
                                    <span>{product.name}</span>
                                    <span className="badge bg-primary rounded-pill">{product.price} €</span>
                                  </button>
                                ))}
                              </ul>
                            )}
                          </div>

                          <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => { setExpandedSaleId(null); setEditingSale(null); }}>Annuler</button>
                            <button className="btn btn-outline-primary" onClick={handleSaveChanges}><i className="fas fa-save me-2"></i>Enregistrer</button>
                            <button className="btn btn-success" onClick={handleSaveAndPrint}><i className="fas fa-print me-2"></i>Enregistrer & Imprimer</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {(!sales || sales.length === 0) && (
              <tr>
                <td colSpan="8" className="text-center py-4 text-muted">Aucune vente ou devis trouvé.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

